// Pure half of the workbook index upload: sparse cell encoding of an Office.js grid and gzip of the JSON body.
// No Office.js here (the executor reads; this module shapes).
import type { IndexCell, IndexCellStyle, IndexValidation } from '../../types/index';

export interface GridStyles {
  /** Per cell of the styled sub-range (top-left aligned with the grid): bold, indent, font colour, fill. */
  bold?: boolean[][];
  indent?: number[][];
  fontColor?: (string | null)[][];
  fill?: (string | null)[][];
}

const isFormula = (f: unknown): f is string => typeof f === 'string' && f.startsWith('=');
/** An array-entered formula comes back braced: `{=MATCH(1, --(A1:Z1 >= B2), 0)}`. */
const unbraced = (f: string): string => (f.startsWith('{=') && f.endsWith('}') ? f.slice(1, -1) : f);
const isBlank = (v: unknown): boolean => v === null || v === undefined || v === '';

/** "#RRGGBB" / "RRGGBB" → "RRGGBB", null for black / white / empty. */
export function colorHex(c: string | null | undefined): string | null {
  if (!c) return null;
  const hex = c.replace('#', '').toUpperCase();
  if (hex.length < 6 || hex === '000000' || hex === 'FFFFFF') return null;
  return hex.slice(-6);
}

/**
 * Encode the populated cells of a grid read from Office.js (`values`, `formulas`, `formulasR1C1`, `numberFormat`
 * of the same range) as payload cells. `rowIndex` / `colIndex` are the 0-based origin of the grid.
 */
export function encodeCells(values: unknown[][], formulas: unknown[][], r1c1: unknown[][], formats: string[][], rowIndex: number, colIndex: number, styles?: GridStyles): IndexCell[] {
  const out: IndexCell[] = [];
  for (let r = 0; r < values.length; r++) {
    const vrow = values[r] ?? [];
    for (let c = 0; c < vrow.length; c++) {
      // A cell holds a formula when either grid says so. Office does not always answer `formulas` for an
      // array-entered (CSE) cell, and a sheet's Index Factor rows — `MATCH(1, --((J12:OO12) >= …), 0)`,
      // array-entered — came back as plain 1s in every version of one client's index. The server then read
      // them as constants: a copy of that block was never re-aimed at the rows it reads, and the new block
      // computed #N/A off blank rows seven below the ones it meant.
      const raw = formulas[r]?.[c];
      const rawRc = r1c1[r]?.[c];
      const braced = typeof raw === 'string' ? unbraced(raw) : raw;
      const bracedRc = typeof rawRc === 'string' ? unbraced(rawRc) : rawRc;
      const formula = isFormula(braced) ? braced : null;
      const value = vrow[c];
      if (!formula && !isFormula(bracedRc) && isBlank(value)) continue;
      const rc = bracedRc;
      const fmt = formats[r]?.[c];
      let style: IndexCellStyle | null = null;
      if (styles) {
        const s: IndexCellStyle = {};
        if (styles.bold?.[r]?.[c]) s.b = 1;
        const indent = styles.indent?.[r]?.[c];
        if (indent && indent > 0) s.i = indent;
        const color = colorHex(styles.fontColor?.[r]?.[c]);
        if (color) s.c = color;
        const fill = colorHex(styles.fill?.[r]?.[c]);
        if (fill) s.f = fill;
        if (Object.keys(s).length) style = s;
      }
      // The R1C1 form is kept whenever it is one, even if `formulas` gave nothing: the server rebuilds the A1
      // form from it, and a formula it cannot see is a formula it treats as a typed number.
      out.push([rowIndex + r + 1, colIndex + c + 1, isBlank(value) ? null : value, formula,
                isFormula(rc) ? rc : null, fmt && fmt !== 'General' ? fmt : null, style]);
    }
  }
  return out;
}

/** The non-blank values of a grid as `[row, col, value]` (1-based), blanks left out as `encodeCells` leaves them:
 *  a values-only upload (`IndexSheetValues`). */
export function encodeValues(values: unknown[][], rowIndex: number, colIndex: number): [number, number, unknown][] {
  const out: [number, number, unknown][] = [];
  for (let r = 0; r < values.length; r++) {
    const vrow = values[r] ?? [];
    for (let c = 0; c < vrow.length; c++) {
      if (!isBlank(vrow[c])) out.push([rowIndex + r + 1, colIndex + c + 1, vrow[c]]);
    }
  }
  return out;
}

/** Expand hidden spans ("5:7", "12:12") into row / column numbers, capped. */
export function expandSpans(spans: string[], cap = 5000): number[] {
  const out: number[] = [];
  for (const span of spans) {
    const [a, b] = span.split(':');
    const lo = toIndex(a);
    const hi = toIndex(b ?? a);
    for (let i = lo; i <= hi && out.length < cap; i++) out.push(i);
  }
  return out;
}

/** Office's data validation types in the payload's words (sculpt-api `xlsx._VALIDATION_TYPES`). `None` (any value),
 *  `Inconsistent` and `MixedCriteria` (cells under different rules) are no rule. */
const VALIDATION_TYPES: Record<string, string> = {
  WholeNumber: 'whole',
  Decimal: 'decimal',
  List: 'list',
  Date: 'date',
  Time: 'time',
  TextLength: 'text_length',
  Custom: 'custom',
};

export function validationType(office: string): string | null {
  return VALIDATION_TYPES[office] ?? null;
}

/** Office's answer for a range whose cells are under different rules: the range must be cut to read them. */
export function isMixedValidation(office: string): boolean {
  return office === 'Inconsistent' || office === 'MixedCriteria';
}

/** A box of cells, 0-based as `getRangeByIndexes` takes it. */
export interface CellBox {
  row: number;
  col: number;
  rows: number;
  cols: number;
}

/** A box cut in two across its longer side; a single cell is not cut. */
export function halves(b: CellBox): [CellBox, CellBox] | null {
  if (b.rows <= 1 && b.cols <= 1) return null;
  if (b.rows >= b.cols) {
    const top = Math.ceil(b.rows / 2);
    return [{ ...b, rows: top }, { ...b, row: b.row + top, rows: b.rows - top }];
  }
  const left = Math.ceil(b.cols / 2);
  return [{ ...b, cols: left }, { ...b, col: b.col + left, cols: b.cols - left }];
}

/** Areas under one rule each, merged by rule (type and source): one entry per rule, its areas space-separated as
 *  Excel writes a validation's `sqref`, in the order first seen. */
export function groupValidations(pieces: { address: string; type: string; source: string | null }[]): IndexValidation[] {
  const out = new Map<string, IndexValidation>();
  for (const p of pieces) {
    const key = JSON.stringify([p.type, p.source]);
    const rule = out.get(key);
    if (rule) rule.at += ` ${p.address}`;
    else out.set(key, { at: p.address, type: p.type, source: p.source });
  }
  return [...out.values()];
}

function toIndex(part: string): number {
  if (/^\d+$/.test(part)) return Number(part);
  let n = 0;
  for (const ch of part.toUpperCase()) n = n * 26 + (ch.charCodeAt(0) - 64);
  return n;
}

export interface EncodedBody {
  body: Uint8Array | string;
  encoding: 'gzip' | null;
  bytes: number;
}

/** Compression must never stall the index: past this delay the sheet goes up as plain JSON. */
export const GZIP_TIMEOUT_MS = 8000;

/** JSON of `obj`, gzipped when the WebView offers CompressionStream (Edge WebView2, WKWebView ≥ 16.4). */
export async function gzipJson(obj: unknown): Promise<EncodedBody> {
  const json = JSON.stringify(obj);
  if (typeof CompressionStream === 'undefined' || typeof ReadableStream === 'undefined' || typeof TextEncoder === 'undefined') {
    return { body: json, encoding: null, bytes: json.length };
  }
  const plain: EncodedBody = { body: json, encoding: null, bytes: json.length };
  let timer: ReturnType<typeof setTimeout> | undefined;
  const timeout = new Promise<EncodedBody>((resolve) => {
    timer = setTimeout(() => resolve(plain), GZIP_TIMEOUT_MS);
  });
  try {
    return await Promise.race([compress(json), timeout]);
  } catch {
    return plain;
  } finally {
    if (timer) clearTimeout(timer);
  }
}

async function compress(json: string): Promise<EncodedBody> {
  try {
    const bytes = new TextEncoder().encode(json);
    const source = new ReadableStream<BufferSource>({
      start(controller) {
        controller.enqueue(bytes);
        controller.close();
      },
    });
    const reader = source.pipeThrough(new CompressionStream('gzip')).getReader();
    const chunks: Uint8Array[] = [];
    let total = 0;
    for (;;) {
      const { done, value } = await reader.read();
      if (done) break;
      const chunk = value instanceof Uint8Array ? value : new Uint8Array(value as ArrayBuffer);
      chunks.push(chunk);
      total += chunk.length;
    }
    const out = new Uint8Array(total);
    let offset = 0;
    for (const chunk of chunks) {
      out.set(chunk, offset);
      offset += chunk.length;
    }
    return { body: out, encoding: 'gzip', bytes: total };
  } catch {
    return { body: json, encoding: null, bytes: json.length };
  }
}
